import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ApprovalState, PipeClassViewModel, EsraPipingAPIClient, RoleViewModel, UserType } from '../../shared/models/autogenerated-piping';
import { UserService } from 'src/app/core/services/user.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { GraphService } from 'src/app/core/services/graph.service';
import * as microsoftgraph from '@microsoft/microsoft-graph-types';
import { catchError, map } from 'rxjs/operators';

@Component({
  selector: 'app-general-details',
  templateUrl: './general-details.component.html',
  styleUrls: ['./general-details.component.scss'],
  standalone: false
})
export class GeneralDetailsComponent implements OnInit {
  @Input() pipeClassViewModel: PipeClassViewModel;
  @Input() canEdit$: Observable<boolean>;
  @Output() userRoleChanged = new EventEmitter<any>();
  @Output() descriptionChanged = new EventEmitter<string>();
  @Output() saveRevisionMembersRequested = new EventEmitter<void>();

  // Accordion states
  generalDetailsExpanded = true;
  rolesExpanded = true;

  // Role lists stored as RoleViewModel to preserve id, approvalState, and all BE fields.
  // The template accesses userDisplayName for display and userId for tracking.
  ownerRoles: RoleViewModel[] = [];
  reviewerRoles: RoleViewModel[] = [];
  contributorRoles: RoleViewModel[] = [];

  // Description editing
  isEditingDescription = false;
  descriptionText = '';
  authorName$: Observable<string>;

  /**
   * Roles are read-only when the pipe class is loaded through a release object group.
   * In this context, role edits must be managed from the release details flow.
   */
  get rolesReadOnlyFromReleaseObjectGroup(): boolean {
    const releaseObjectGroup: object | undefined = this.pipeClassViewModel?.releaseObjectGroup as object | undefined;
    if (!releaseObjectGroup) {
      return false;
    }

    return Object.keys(releaseObjectGroup).length > 0;
  }

  constructor(
    private userService: UserService,
    private snackBarService: SnackBarService,
    private apiClient: EsraPipingAPIClient,
    private cdr: ChangeDetectorRef,
    private graphService: GraphService
  ) {}

  ngOnInit(): void {
    this.initializeData();
  }

  private initializeData(): void {
    if (this.pipeClassViewModel) {
      this.descriptionText = this.pipeClassViewModel.fromRevision?.description || '';
      this.ownerRoles = [];
      this.reviewerRoles = [];
      this.contributorRoles = [];
      this.loadRevisionMembers();
      this.loadAuthorName();
    }
  }

  private loadAuthorName(): void {
    const authorOid = this.pipeClassViewModel?.fromRevision?.authorOid;

    if (authorOid) {
      this.authorName$ = this.graphService.getUserByOid(authorOid).pipe(
        map(user => user?.displayName || 'Unknown Author'),
        catchError(() => of('Unknown Author'))
      );
    } else {
      this.authorName$ = of(this.pipeClassViewModel?.fromRevision?.authorName || 'Unknown Author');
    }
  }

  private loadRevisionMembers(): void {
    const revisionId = this.pipeClassViewModel.fromRevision.id;
    this.apiClient.getRevisionMembers(revisionId, this.pipeClassViewModel.id).subscribe({
      next: (roles: RoleViewModel[]) => {
        this.processRevisionRoles(roles);
      },
      error: (error) => {
        console.error('Error loading revision members:', error);
        this.snackBarService.showSnackBar(
          true,
          'Error loading revision members',
          'mat-snack-bar-error'
        );
      }
    });
  }

  private processRevisionRoles(roles: RoleViewModel[]): void {
    this.ownerRoles = [];
    this.reviewerRoles = [];
    this.contributorRoles = [];

    roles.forEach(role => {
      switch (role.userRole) {
        case UserType._1: // APPROVER — shown as "revision owners" in the panel
          this.ownerRoles.push(role);
          break;
        case UserType._2: // INTERNAL_REVIEWER
          this.reviewerRoles.push(role);
          break;
        case UserType._3: // EXTERNAL_REVIEWER
          this.contributorRoles.push(role);
          break;
        default:
          // UserType._0 (AUTHOR) is managed by the BE via authorOid — not shown in the editable panel
          break;
      }
    });
    this.cdr.markForCheck();
  }

  // General Details methods
  startEditDescription(): void {
    this.isEditingDescription = true;
  }

  saveDescription(): void {
    if (this.descriptionText.trim() !== '') {
      if (this.pipeClassViewModel.fromRevision) {
        this.pipeClassViewModel.fromRevision.description = this.descriptionText;
      }
      this.descriptionChanged.emit(this.descriptionText);
      this.isEditingDescription = false;
    }
  }

  cancelEditDescription(): void {
    this.descriptionText = this.pipeClassViewModel.fromRevision?.description || '';
    this.isEditingDescription = false;
  }

  /**
   * Sends the current role lists to PUT /Revision/{id}/updatemembers.
   * RoleViewModel objects are sent as-is so the BE receives the original id,
   * approvalState, and all other fields — preventing duplicate creation and
   * unintended approval state resets.
   * New members added via addRevisionApproval have id undefined, which signals
   * the BE to create a new record.
   */
  saveRevisionMembers(): void {
    if (!this.pipeClassViewModel?.fromRevision?.id) {
      this.snackBarService.showError('Error: No revision ID available');
      return;
    }

    const revisionId = this.pipeClassViewModel.fromRevision.id;
    const roleViewModels: RoleViewModel[] = [
      ...this.ownerRoles,
      ...this.reviewerRoles,
      ...this.contributorRoles,
    ];

    this.apiClient.updateRevisionMembers(revisionId, roleViewModels).subscribe({
      next: (updatedRevision) => {
        // Re-process the returned roles so local state stays in sync with the BE
        if (updatedRevision?.roles?.length) {
          this.processRevisionRoles(updatedRevision.roles);
        }
        this.snackBarService.showInfo('Revision members saved successfully');
      },
      error: (error) => {
        this.snackBarService.showError('Error saving revision members: ' + (error.message || 'Unknown error'));
      }
    });
  }

  addRevisionApproval(user: microsoftgraph.User, role: string): void {
    if (this.rolesReadOnlyFromReleaseObjectGroup) {
      return;
    }

    const list = this.getRoleList(role);
    const alreadyExists = list.some(r => r.userId === user.id);

    if (alreadyExists) {
      this.snackBarService.showError(`${user.displayName} is already a ${role}`);
      return;
    }

    // New member: no id yet — the BE will create the record on save
    const newRole = new RoleViewModel({
      userId: user.id!,
      revisionId: this.pipeClassViewModel.fromRevision.id,
      userRole: this.userTypeForRole(role),
      userDisplayName: user.displayName,
      userEmail: user.userPrincipalName,
      approvalState: ApprovalState._0, // Pending — default for new members
    });
    list.push(newRole);
    this.userRoleChanged.emit({ action: 'add', user, role });
  }

  removeMember(roleToRemove: RoleViewModel, role: string): void {
    if (this.rolesReadOnlyFromReleaseObjectGroup) {
      return;
    }

    const list = this.getRoleList(role);
    const idx = list.findIndex(r => r.userId === roleToRemove.userId);
    if (idx !== -1) {
      list.splice(idx, 1);
    }
    this.userRoleChanged.emit({ action: 'remove', user: { id: roleToRemove.userId, displayName: roleToRemove.userDisplayName }, role });
  }

  private getRoleList(role: string): RoleViewModel[] {
    if (role === 'owner') { return this.ownerRoles; }
    if (role === 'reviewer') { return this.reviewerRoles; }
    return this.contributorRoles;
  }

  private userTypeForRole(role: string): UserType {
    if (role === 'owner') { return UserType._1; }  // APPROVER
    if (role === 'reviewer') { return UserType._2; } // INTERNAL_REVIEWER
    return UserType._3; // EXTERNAL_REVIEWER
  }

  getCurrentUserName(): string {
    return this.userService.getCurrentUser()?.name;
  }

  /**
   * True when the currently logged-in user is the author of this revision.
   * Only the author can remove/replace revision owners — the BE protects the AUTHOR role
   * so non-authors would see a phantom remove that reverts on reload.
   */
  get currentUserIsAuthor(): boolean {
    const currentOid = this.userService.getCurrentUser()?.idTokenClaims?.['oid'] as string | undefined;
    const authorOid = this.pipeClassViewModel?.fromRevision?.authorOid;
    return !!currentOid && !!authorOid && currentOid === authorOid;
  }

  /** Returns the current combined role list for use in saveRevisionDraft payloads. */
  getCurrentMembers(): RoleViewModel[] {
    return [
      ...this.ownerRoles,
      ...this.reviewerRoles,
      ...this.contributorRoles,
    ];
  }

  /** Re-syncs local role arrays from a BE response after a save, keeping ids and approvalStates in sync. */
  updateRolesFromResponse(roles: RoleViewModel[]): void {
    this.processRevisionRoles(roles);
  }

  trackByUserId(index: number, role: RoleViewModel): string {
    return role.userId || index.toString();
  }

  get revisionDescription(): string {
    return this.pipeClassViewModel?.fromRevision?.description || '';
  }
}
